import {autoserializeAs, inheritSerialization} from "dcerialize";
import {nonEmpty} from "../../helpers/OptionalStringDecorators";
import type {Existing, IdentifiableModel, IdOf, New} from "../base/IdentifiableModel";
import {createExisting, createNew} from "../base/IdentifiableModel";
import type {ModificationTrackedModel} from "../base/ModificationTrackedModel";
import type {SoftDeletableModel} from "../base/SoftDeletableModel";
import {CodeOnlyCountry, SimpleCountry} from "../i18n/Country";
import {CodeOnlyLanguage, SimpleLanguage} from "../i18n/Language";
import {ModificationDetails} from "../ModificationDetails";
import {IdOnlyBactoforceArea, SimpleBactoforceArea} from "./BactoforceArea";

export class IdOnlyBactoforceLocation implements IdentifiableModel {
  @autoserializeAs(() => Number)
  public id?: number;

  public static existing(id: IdOf<IdOnlyBactoforceLocation>): Existing<IdOnlyBactoforceLocation> {
    return createExisting(IdOnlyBactoforceLocation, id);
  }
}

@inheritSerialization(() => IdOnlyBactoforceLocation)
export class SimpleBactoforceLocation extends IdOnlyBactoforceLocation implements SoftDeletableModel {
  @autoserializeAs(() => String)
  public name: string;
  @autoserializeAs(() => Boolean)
  public readonly isDeleted: boolean;
  @autoserializeAs(() => IdOnlyBactoforceArea)
  public area?: Existing<IdOnlyBactoforceArea>;
}

@inheritSerialization(() => SimpleBactoforceLocation)
export class WritableBactoforceLocation extends SimpleBactoforceLocation {
  @autoserializeAs(() => String)
  @nonEmpty
  public number?: string;
  @autoserializeAs(() => String)
  @nonEmpty
  public email?: string;
  @autoserializeAs(() => String)
  @nonEmpty
  public phone?: string;
  @autoserializeAs(() => String)
  @nonEmpty
  public fax?: string;
  @autoserializeAs(() => String)
  @nonEmpty
  public street?: string;
  @autoserializeAs(() => String)
  @nonEmpty
  public zip?: string;
  @autoserializeAs(() => String)
  @nonEmpty
  public city?: string;
  @autoserializeAs(() => CodeOnlyCountry)
  public country: CodeOnlyCountry;
  @autoserializeAs(() => CodeOnlyLanguage)
  public language: CodeOnlyLanguage;
  @autoserializeAs(() => Boolean)
  public automaticInvoicingEnabled: boolean;
  @autoserializeAs(() => Number)
  public exactNumber?: number;
  @autoserializeAs(() => String)
  @nonEmpty
  public footer1?: string;
  @autoserializeAs(() => String)
  @nonEmpty
  public footer2?: string;

  public static createNew(
    name: string,
    country: Readonly<CodeOnlyCountry>,
    language: Readonly<CodeOnlyLanguage>,
    automaticInvoicingEnabled: true,
    exactNumber: number): New<WritableBactoforceLocation>;
  public static createNew(
    name: string,
    country: Readonly<CodeOnlyCountry>,
    language: Readonly<CodeOnlyLanguage>,
    automaticInvoicingEnabled: false,
    exactNumber?: number): New<WritableBactoforceLocation>;
  public static createNew(
    name: string,
    country: Readonly<CodeOnlyCountry>,
    language: Readonly<CodeOnlyLanguage>,
    automaticInvoicingEnabled: boolean,
    exactNumber?: number): New<WritableBactoforceLocation> {
    if (automaticInvoicingEnabled && exactNumber === undefined)
      throw new Error("exactNumber must be defined when automaticInvoicingEnabled is true");
    const location = Object.assign(createNew(WritableBactoforceLocation), {isDeleted: false});
    location.name = name;
    location.country = country;
    location.language = language;
    location.automaticInvoicingEnabled = automaticInvoicingEnabled;
    location.exactNumber = exactNumber;
    return location;
  }
}

/**
 * @sealed
 */
@inheritSerialization(() => WritableBactoforceLocation)
export class BactoforceLocation extends WritableBactoforceLocation implements ModificationTrackedModel {
  @autoserializeAs(() => SimpleBactoforceArea)
  public override area?: Existing<SimpleBactoforceArea>;
  @autoserializeAs(() => SimpleCountry)
  public override country: SimpleCountry;
  @autoserializeAs(() => SimpleLanguage)
  public override language: SimpleLanguage;

  @autoserializeAs(() => ModificationDetails)
  public readonly modificationDetails?: ModificationDetails;
}
